import { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { useResourceMutation } from './use-resource-mutation.hook'
import { useResourceQuery } from './use-resource-query.hook'
import { BaseRequirementLegalBasis } from '@shared/types'

export function useHandleResourceState<ResourceType extends object = any>({
  resourceEndpoint,
  resourceID,
}: {
  resourceEndpoint: string
  resourceID?: string
}) {
  const isResourceEdit = Boolean(resourceID)
  const formMethods = useForm<ResourceType | any>({
    defaultValues: { legalBasis: [] },
  })

  const { resourceData, readResourceData, isResourceDataFetching } =
    useResourceQuery(resourceEndpoint, resourceID)

  const {
    createResource,
    isResourceCreating,
    isResourceCreationFailed,
    isResourceCreationSucceed,
    createResourceErrors,
    resetResourceCreationState,
  } = useResourceMutation({
    resourceEndpoint: resourceEndpoint,
    resourceID: resourceID,
  })

  useEffect(() => {
    if (isResourceEdit) {
      readResourceData()
    }
  }, [isResourceEdit, readResourceData])

  const { reset: formMethodsReset, setValue: formMethodsSetValue } = formMethods

  useEffect(() => {
    if (isResourceEdit && resourceData) {
      setTimeout(() => {
        formMethodsReset({ ...resourceData })

        formMethodsSetValue('requirementBase', resourceData['requirementBase'])
      }, 10)
    }
  }, [isResourceEdit, resourceData, formMethodsReset, formMethodsSetValue])

  const onAddResource = useCallback(
    (additionalData = {}) => {
      // NOTE: clear "event" objects
      if (
        typeof additionalData === 'object' &&
        additionalData.hasOwnProperty('_reactName')
      ) {
        additionalData = {}
      }
      const resourceFormData = formMethods.getValues()

      resetResourceCreationState()
      createResource({
        ...(isResourceEdit ? resourceData : {}),
        ...resourceFormData,
        ...additionalData,
      })
    },
    [
      formMethods,
      createResource,
      resetResourceCreationState,
      isResourceEdit,
      resourceData,
    ]
  )

  return {
    formMethods,
    onAddResource,
    resourceData,
    mutation: {
      isResourceEdit,
      isResourceDataFetching,
      isResourceCreating,
      isResourceCreationFailed,
      isResourceCreationSucceed,
      createResourceErrors,
      resetResourceCreationState,
    },
  }
}
